import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

// ============================================================================
// Értesítések (harang) + Web Push feliratkozás.
// ============================================================================

export type NotificationType =
  | 'order_message' | 'ticket_comment' | 'ticket_assigned' | 'ticket_due' | 'reminder_due' | 'mail_new' | 'mail_reply';

export interface AppNotification {
  id: number;
  type: NotificationType;
  title: string;
  body: string;
  /** ERP-n belüli útvonal, pl. /support?ticket=12 */
  link: string;
  actor: { id: number; name: string; image: string | null } | null;
  createdAt: string;
  readAt: string | null;
}

/** A push állapota ezen az eszközön. */
export type PushState = 'unsupported' | 'denied' | 'off' | 'on';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  /** olvasatlan értesítések száma — a harang jelvénye */
  readonly unread$ = new BehaviorSubject<number>(0);
  readonly pushState$ = new BehaviorSubject<PushState>('unsupported');
  private polling = false;

  constructor(private http: HttpClient, private swPush: SwPush) {}

  /** Indítás bejelentkezés után: számláló-frissítés percenként, előtérbe kerüléskor és push érkezésekor. */
  start(): void {
    if (this.polling) {
      return;
    }
    this.polling = true;
    timer(0, 60 * 1000).subscribe(() => this.refreshUnread());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.refreshUnread();
      }
    });
    if (this.swPush.isEnabled) {
      this.swPush.messages.subscribe(() => this.refreshUnread());
    }
    this.refreshPushState();
  }

  refreshUnread(): void {
    this.http.get<{ unread: number }>('notifications/unread-count')
      .pipe(catchError(() => of({ unread: this.unread$.value })))
      .subscribe((result) => this.unread$.next(result?.unread || 0));
  }

  list(onlyUnread: boolean): Observable<AppNotification[]> {
    return this.http.get<AppNotification[]>(`notifications${onlyUnread ? '?unread=1' : ''}`);
  }

  markRead(id: number, read = true): Observable<AppNotification> {
    return this.http.patch<AppNotification>(`notifications/${id}`, { read }).pipe(tap(() => this.refreshUnread()));
  }

  markAllRead(): Observable<{ updated: number }> {
    return this.http.post<{ updated: number }>('notifications/read-all', {}).pipe(tap(() => this.unread$.next(0)));
  }

  remove(id: number): Observable<void> {
    return this.http.delete<void>(`notifications/${id}`).pipe(tap(() => this.refreshUnread()));
  }

  removeAllRead(): Observable<{ deleted: number }> {
    return this.http.delete<{ deleted: number }>('notifications/read');
  }

  // ---------------------------------------------------------------- web push

  /** Támogatott-e a push ezen az eszközön (iPhone-on csak a kezdőképernyőre telepített appban). */
  get pushSupported(): boolean {
    return this.swPush.isEnabled && 'Notification' in window && 'PushManager' in window;
  }

  async refreshPushState(): Promise<void> {
    if (!this.pushSupported) {
      this.pushState$.next('unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      this.pushState$.next('denied');
      return;
    }
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      this.pushState$.next(sub ? 'on' : 'off');
      // A böngésző oldalán élő feliratkozást a szerver is ismerje (pl. új belépés után).
      if (sub) {
        this.http.post('notifications/push/subscribe', sub.toJSON()).subscribe({ error: () => undefined });
      }
    } catch {
      this.pushState$.next('off');
    }
  }

  /** Engedélykérés + feliratkozás; a hibát szöveggel adja vissza. */
  async enablePush(): Promise<string | null> {
    if (!this.pushSupported) {
      return 'Ezen az eszközön a böngésző nem támogatja az értesítéseket.';
    }
    try {
      const { publicKey } = await this.http.get<{ publicKey: string }>('notifications/push/public-key').toPromise() as { publicKey: string };
      const sub = await this.swPush.requestSubscription({ serverPublicKey: publicKey });
      await this.http.post('notifications/push/subscribe', sub.toJSON()).toPromise();
      this.pushState$.next('on');
      return null;
    } catch (error: any) {
      await this.refreshPushState();
      if (Notification.permission === 'denied') {
        return 'Az értesítések le vannak tiltva ehhez az oldalhoz — a böngésző beállításaiban engedélyezd.';
      }
      return `Nem sikerült feliratkozni: ${error?.message || error}`;
    }
  }

  async disablePush(): Promise<void> {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await this.http.post('notifications/push/unsubscribe', { endpoint: sub.endpoint }).toPromise();
        await sub.unsubscribe();
      }
    } finally {
      this.pushState$.next('off');
    }
  }

  sendTest(): Observable<{ sent: number }> {
    return this.http.post<{ sent: number }>('notifications/push/test', {});
  }
}
